import { supabase } from './supabase.js';

/** Select embebido: valoración + reseñante. */
export const REVIEW_RECEIVED_LIST_SELECT = `
  id,
  rating,
  tags,
  comment,
  created_at,
  is_visible,
  seller_public_reply,
  seller_public_reply_at,
  transaction_id,
  reviewer:profiles!reviews_reviewer_id_fkey ( username, avatar_url )
`;

/**
 * Valoraciones recibidas por un perfil (orden recientes primero).
 * @param {string} reviewedProfileId
 * @param {{ onlyVisible?: boolean, limit?: number }} [opts]
 */
export async function fetchReceivedReviews(reviewedProfileId, opts = {}) {
  const onlyVisible = opts.onlyVisible !== false;
  const limit = Number(opts.limit) > 0 ? Number(opts.limit) : 120;
  if (!reviewedProfileId) return { data: [], error: 'missing_profile' };

  let q = supabase
    .from('reviews')
    .select(REVIEW_RECEIVED_LIST_SELECT)
    .eq('reviewed_id', reviewedProfileId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (onlyVisible) q = q.eq('is_visible', true);

  const { data, error } = await q;
  if (error) {
    console.error('[reviewsService] fetchReceivedReviews:', error.message);
    return { data: [], error: error.message };
  }
  return { data: data ?? [], error: null };
}

/**
 * Actualiza la respuesta pública del valorado (RLS + trigger).
 * @param {string} reviewedUserId — debe coincidir con sesión
 * @param {string} reviewId
 * @param {string|null} replyText — null o vacío borra la respuesta
 */
export async function updateSellerPublicReply(reviewedUserId, reviewId, replyText) {
  if (!reviewedUserId || !reviewId) return { error: 'Parámetros incompletos' };
  const trimmed = replyText != null ? String(replyText).trim() : '';
  const payload =
    trimmed === ''
      ? { seller_public_reply: null, seller_public_reply_at: null }
      : { seller_public_reply: trimmed, seller_public_reply_at: new Date().toISOString() };

  const { error } = await supabase.from('reviews').update(payload).eq('id', reviewId).eq('reviewed_id', reviewedUserId);

  if (error) {
    console.error('[reviewsService] updateSellerPublicReply:', error.message);
    return { error: error.message };
  }
  return { error: null };
}

export const REVIEW_GIVEN_SELECT = `
  id,
  rating,
  tags,
  comment,
  created_at,
  is_visible,
  transaction_id,
  reviewed_id,
  role,
  reviewed:profiles!reviews_reviewed_id_fkey ( username, avatar_url )
`;

/**
 * Valoraciones emitidas por el usuario.
 */
export async function fetchGivenReviews(reviewerId, opts = {}) {
  const limit = Number(opts.limit) > 0 ? Number(opts.limit) : 80;
  if (!reviewerId) return { data: [], error: 'missing_profile' };
  const { data, error } = await supabase
    .from('reviews')
    .select(REVIEW_GIVEN_SELECT)
    .eq('reviewer_id', reviewerId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.error('[reviewsService] fetchGivenReviews:', error.message);
    return { data: [], error: error.message };
  }
  return { data: data ?? [], error: null };
}

/**
 * Transacciones completadas sin valoración del usuario actual.
 */
export async function fetchPendingReviewTransactions(userId) {
  if (!userId) return { data: [], error: 'missing_user' };
  const { data: txs, error: e1 } = await supabase
    .from('transactions')
    .select(
      `
      id,
      listing_id,
      buyer_id,
      seller_id,
      amount,
      shipping_type,
      buyer_confirmed_at,
      created_at,
      listings:listing_id ( id, title, artist, condition, price, photos, cover_url ),
      buyer:profiles!transactions_buyer_id_fkey ( id, username, avatar_url ),
      seller:profiles!transactions_seller_id_fkey ( id, username, avatar_url )
    `
    )
    .not('buyer_confirmed_at', 'is', null)
    .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
    .order('buyer_confirmed_at', { ascending: false })
    .limit(40);
  if (e1) {
    console.error('[reviewsService] fetchPendingReviewTransactions txs:', e1.message);
    return { data: [], error: e1.message };
  }
  const list = txs ?? [];
  if (!list.length) return { data: [], error: null };

  const txIds = list.map(t => t.id);
  const { data: existing, error: e2 } = await supabase
    .from('reviews')
    .select('transaction_id')
    .eq('reviewer_id', userId)
    .in('transaction_id', txIds);
  if (e2) {
    console.error('[reviewsService] fetchPendingReviewTransactions reviews:', e2.message);
    return { data: [], error: e2.message };
  }
  const done = new Set((existing ?? []).map(r => r.transaction_id));
  const pending = list
    .filter(t => !done.has(t.id))
    .map(t => {
      const asBuyer = t.buyer_id === userId;
      const counterparty = asBuyer
        ? (Array.isArray(t.seller) ? t.seller[0] : t.seller)
        : (Array.isArray(t.buyer) ? t.buyer[0] : t.buyer);
      const listing = Array.isArray(t.listings) ? t.listings[0] : t.listings;
      return {
        transactionId: t.id,
        role: asBuyer ? 'buyer' : 'seller',
        amount: t.amount,
        shippingType: t.shipping_type,
        confirmedAt: t.buyer_confirmed_at,
        listing,
        counterparty,
      };
    });
  return { data: pending, error: null };
}

/**
 * Envía valoración tras venta completada (RPC; revelación mutua en servidor).
 */
export async function createTransactionReview({ transactionId, rating, tags, comment }) {
  if (!transactionId) return { reviewId: null, error: 'Falta el pedido.' };
  const { data, error } = await supabase.rpc('create_transaction_review', {
    p_transaction_id: transactionId,
    p_rating: rating,
    p_tags: tags ?? [],
    p_comment: comment ?? null,
  });
  if (error) {
    console.error('[reviewsService] createTransactionReview:', error.message);
    const msg = String(error.message || '');
    if (msg.includes('transaction_not_completed')) {
      return { reviewId: null, error: 'Solo puedes valorar cuando el pedido está completado.' };
    }
    if (msg.includes('duplicate') || msg.includes('unique')) {
      return { reviewId: null, error: 'Ya has valorado esta transacción.' };
    }
    return { reviewId: null, error: error.message || 'No se pudo enviar la valoración.' };
  }
  return { reviewId: data, error: null };
}
